import { motion } from "framer-motion";
import {
    Home,
    Crown,
    Ruler,
    Compass,
    Sofa,
    Building2,
    Layers3,
    PackageOpen,
    Zap,
    Droplets,
    Hammer,
    PartyPopper,
} from "lucide-react";

const SERVICES = [
    { icon: Home, title: "Residential Construction", desc: "Ground-up homes with structural integrity & modern design." },
    { icon: Crown, title: "Luxury Home Construction", desc: "Premium materials, designer detailing, signature finishes.", featured: true },
    { icon: Ruler, title: "2D & 3D Architectural Planning", desc: "Photo-real concepts before a single brick is laid." },
    { icon: Compass, title: "Vastu-Based Home Design", desc: "Energy-aligned layouts rooted in tradition." },
    { icon: Sofa, title: "Interior Design", desc: "Curated spaces — warm, livable, photogenic." },
    { icon: Building2, title: "Exterior Elevation Design", desc: "Façades that turn heads on the street." },
    { icon: Layers3, title: "Flooring Solutions", desc: "Italian marble, premium tiles, engineered wood." },
    { icon: PackageOpen, title: "Modular Furniture", desc: "Factory-finished kitchens, wardrobes & storage." },
    { icon: Zap, title: "Electrical Work", desc: "Branded fittings, smart-home ready wiring." },
    { icon: Droplets, title: "Plumbing Work", desc: "Leak-proof CPVC systems with branded fixtures." },
    { icon: Hammer, title: "Renovation & Remodeling", desc: "Transform old spaces into modern dream homes." },
    { icon: PartyPopper, title: "Housewarming Event Support", desc: "From puja setup to your first guests — handled." },
];

export default function Services() {
    return (
        <section id="services" className="relative bg-brand-bone py-20 md:py-32" data-testid="services-section">
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-8 h-px bg-brand-orange" />
                            <span className="text-[10px] uppercase tracking-[0.25em] text-brand-orange font-medium">Services</span>
                        </div>
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="font-display font-semibold text-brand-ink text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight"
                            data-testid="services-heading"
                        >
                            Twelve crafts.{" "}
                            <span className="font-serif-italic font-normal brand-gradient-text">One promise.</span>
                        </motion.h2>
                    </div>
                    <p className="lg:col-span-5 text-brand-muted text-[15px] leading-relaxed">
                        Everything you need to build, beautify and live in a home you love —
                        delivered under one roof by Udaipur's most trusted construction team.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        const featured = s.featured;
                        return (
                            <motion.article
                                key={s.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                                className={`group relative overflow-hidden rounded-2xl p-6 transition-colors duration-300 flex flex-col ${
                                    featured
                                        ? "bg-brand-ink text-white"
                                        : "bg-white border border-brand-line hover:border-brand-ink/20"
                                }`}
                                data-testid={`service-card-${i}`}
                            >
                                {featured && (
                                    <div className="absolute -top-32 -right-24 w-72 h-72 rounded-full brand-gradient opacity-60 blur-3xl pointer-events-none" />
                                )}

                                <div className="relative flex items-start justify-between mb-10">
                                    <div
                                        className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                                            featured
                                                ? "bg-white/15 text-white"
                                                : "bg-brand-bone text-brand-ink group-hover:bg-brand-ink group-hover:text-white"
                                        }`}
                                    >
                                        <Icon size={18} strokeWidth={1.7} />
                                    </div>
                                    <span
                                        className={`text-[10px] tabular-nums tracking-widest ${
                                            featured ? "text-white/40" : "text-brand-line group-hover:text-brand-orange"
                                        } transition-colors`}
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3
                                    className={`relative font-display font-semibold text-[17px] md:text-[19px] leading-tight tracking-tight ${
                                        featured ? "text-white" : "text-brand-ink"
                                    }`}
                                >
                                    {s.title}
                                </h3>
                                <p
                                    className={`relative mt-2 text-[13px] leading-relaxed ${
                                        featured ? "text-white/65" : "text-brand-muted"
                                    }`}
                                >
                                    {s.desc}
                                </p>
                            </motion.article>
                        );
                    })}
                </div>

                {/* SEO keyword strip */}
                <p className="mt-12 text-center text-brand-muted text-[13px] max-w-3xl mx-auto leading-relaxed">
                    Looking for the <strong className="text-brand-ink font-semibold">best construction company in Udaipur</strong> or a trusted <strong className="text-brand-ink font-semibold">luxury home builder in Rajasthan</strong>?
                    Dhanshree Constructions delivers turnkey home construction, interior design,
                    and architectural planning across Udaipur city.
                </p>
            </div>
        </section>
    );
}
